import styled from 'styled-components'
import Select from 'react-select'
import { Colors, Spacing } from 'cio-design-library/design-tokens'
import Typography, { FontWeights } from 'builder/styles/typography'
import { Form } from 'builder/components/SimpleForm'
import { TextField } from 'builder/components/TextField'
import Shadows from 'builder/styles/shadows'
import Icon24 from 'builder/components/Icon'
import Media from 'builder/styles/media'
import { ButtonV2 } from 'builder/components/Button'
import { ReactSelectOption } from '../../types'

export const Container = styled.div`
  margin: 0 56px;

  ${Media.AboveTablet`
    margin: 0 auto;
    width: 100%;
    max-width: 1120px;
  `}

  ${Media.Phone`
    margin: 0 36px;
  `}
`

export const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${Media.AbovePhone`
    margin-bottom: 48px;
  `}
`

export const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.XS};
  margin-bottom: ${Spacing.M};
`

export const Title = styled.p`
  ${Typography.S};
  ${FontWeights.DemiBold};
  color: ${Colors.Neutral90};
`

export const FormContainer = styled(Form)``

export const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Media.AboveTablet`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 64px;
  `}
`

export const PreFillingText = styled.div`
  ${Typography.Caption};
  margin-left: 2px;
  color: ${Colors.Neutral90};
`

export const PreFilledText = styled.div`
  ${Typography.Caption};
  color: ${Colors.Green50};
  margin-left: 2px;
`

export const LabelSpinner = styled.div`
  position: relative;
  margin-right: 10px;
`

export const SpinnerRightBar = styled.div`
  display: flex;
  position: absolute;
  justify-content: end;
  align-items: center;
  gap: 1px;
  top: 0;
  right: 0;
`

export const EmailErrorContainer = styled.div`
  width: 90%;
`

export const ContactSupport = styled.a`
  ${Typography.Caption}
  ${FontWeights.Regular}

  color: ${Colors.Blue50};

  &:hover {
    border-bottom: 1px solid ${Colors.Blue50};
  }
`

export const ToggleLabel = styled.div`
  ${Typography.Body};
`

export const TextFieldAmount = styled(TextField)`
  & input {
    padding-left: 20px;
  }

  & span {
    color: ${Colors.Neutral90};
  }
`

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${Media.Tablet`
    margin-bottom: ${Spacing.S};
  `}
`

export const Description = styled.p`
  ${Typography.Body};
  color: ${Colors.Neutral50};
`

export const TextFieldHint = styled.p`
  width: 100%;
  ${Typography.Caption};
  color: ${Colors.Neutral50};
  margin-top: 4px;
`

export const ActionContainer = styled.div`
  position: fixed;
  bottom: 0;
  border-top: 1px solid ${Colors.Neutral10};
  background: ${Colors.White};
  width: -webkit-fill-available;
  padding: 16px;

  ${Media.Phone`
    border-top: none;
    background-color: unset;
    position: relative;
    padding: 0;
    margin-top: ${Spacing.S};
  `}
`

export const ActionWrapper = styled.div`
  width: 100%;
  max-width: 1120px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${Media.Phone`
    flex-direction: column;
    gap: 20px;
  `}
`

export const StyledSelect = styled(Select<ReactSelectOption>)`
  .select__control {
    border: none;
    outline: none;
    width: 100%;
    min-height: 48px;
    cursor: pointer;
    border-radius: 4px;
    box-shadow: none;
    background-color: ${Colors.Neutral10};
  }

  .select__placeholder {
    color: ${Colors.Neutral50};
  }

  .select__menu {
    margin: 0;
    width: 100%;
    box-shadow: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    background-color: ${Colors.Neutral10};
    border-top: 2px solid ${Colors.Blue50};
  }

  .select__option {
    background-color: transparent;

    &:hover {
      cursor: pointer;
      color: ${Colors.Blue50};
      background-color: ${Colors.Blue20};
    }
  }

  .select__option--is-selected {
    color: ${Colors.Blue60};
    ${FontWeights.Medium}
  }

  .select__multi-value {
    ${Shadows.light.low};
    height: 32px;
    display: flex;
    border-radius: 4px;
    padding: 8px 4px 6px 4px;
    align-items: center;
    background-color: ${Colors.White};
  }

  .select__multi-value__label {
    color: ${Colors.Neutral90};
  }

  .select__multi-value__remove {
    padding: 0;
    color: ${Colors.Neutral40};

    &:hover {
      cursor: pointer;
      color: ${Colors.Blue50};
      background-color: transparent;
    }
  }
`

export const FormGrid = styled.div<{
  $columnGap?: number
  marginTop?: number
  marginBottom?: number
}>`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: ${({ $columnGap }) => `${$columnGap ?? 0}px`};
  margin: ${({ marginTop = 0, marginBottom = 0 }) => `${marginTop}px auto ${marginBottom}px`};
  width: 100%;
`

export const FormGridItem = styled.div<{ $columnSize?: number }>`
  grid-column: ${({ $columnSize }) => `span ${$columnSize}`};
`

export const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.S};
`

export const AvailableCountriesContainer = styled.div`
  width: 100%;
  display: flex;
  gap: ${Spacing['2XS']};
  margin-bottom: 10px;
  margin-top: -8px;
`

export const AvailableCountriesText = styled.p`
  ${Typography.Caption};
  color: ${Colors.Neutral50};
`

export const IconContainer = styled.div`
  display: flex;
`

export const IconInfo = styled(Icon24.Info)`
  width: 20px;
  height: 20px;
  color: ${Colors.Blue50};
`

export const ActionButton = styled(ButtonV2)`
  ${Media.Phone`
    width: 100%;
  `}
`
